/**
 * Mind Map Types
 * TypeScript interfaces for React Flow-based Mind Map
 */

import type { Node, Edge } from "@xyflow/react";

export type MindMapNodeType =
  | "topic"
  | "idea"
  | "note"
  | "goal"
  | "step"
  | "category"
  | "break" // legacy (migrated to junction)
  | "junction";

export interface MindMapNodeData extends Record<string, unknown> {
  label: string;
  description?: string;
  goalDate?: string; // ISO string
  goalAmount?: number; // Goal-only: currency amount in dollars (e.g. 2500.5)
  lockedFromGoalId?: string; // Goal-only: when set, fields mirror Goals page and are read-only
  stepTimeEstimateMinutes?: number; // Step-only: total estimated time in minutes
  associations?: Array<{
    kind: "goal" | "debt" | "expense" | "income" | "housingForecast";
    id: string;
    label: string;
    signedAmount?: number; // Income positive, Expense/Debt negative
    // Housing Forecast linkage: store computed values so nodes can render without
    // needing to fetch profiles.
    housingNetCash?: number; // Net cash after taxes/selling costs/mortgage payoff.
    housingNetCashNoTax?: number; // Net cash excluding capital gains taxes.
    housingCapitalGainsTax?: number; // Estimated total capital gains taxes used in the calculation.
    excludeCapitalGainsTax?: boolean; // When true, use housingNetCashNoTax.
  }>;
  width?: number;
  height?: number;
}

export type MindMapNode = Node<MindMapNodeData, MindMapNodeType>;
export type MindMapEdge = Edge;

export interface MindMapProfile {
  id: string;
  name: string;
  nodes: MindMapNode[];
  edges: MindMapEdge[];
  createdAt: number;
  updatedAt: number;
}

export interface MindMapTemplate {
  id: string;
  name: string;
  description: string;
  nodes: MindMapNode[];
  edges: MindMapEdge[];
  icon: string;
}

export const DEFAULT_NODES: MindMapNode[] = [
  {
    id: "1",
    type: "topic",
    position: { x: 400, y: 200 },
    data: { label: "Central Topic" },
  },
];

export const DEFAULT_EDGES: MindMapEdge[] = [];

export const MINDMAP_TEMPLATES: MindMapTemplate[] = [
  {
    id: "blank",
    name: "Blank",
    description: "Start with a clean canvas",
    nodes: [
      {
        id: "1",
        type: "topic",
        position: { x: 400, y: 200 },
        data: { label: "Topic" },
      },
    ],
    edges: [],
    icon: "add",
  },
  {
    id: "brainstorm",
    name: "Brainstorming",
    description: "Capture and organize ideas",
    nodes: [
      {
        id: "1",
        type: "topic",
        position: { x: 400, y: 200 },
        data: { label: "Brainstorm" },
      },
      {
        id: "2",
        type: "idea",
        position: { x: 200, y: 100 },
        data: { label: "What?" },
      },
      {
        id: "3",
        type: "idea",
        position: { x: 600, y: 100 },
        data: { label: "Why?" },
      },
      {
        id: "4",
        type: "idea",
        position: { x: 200, y: 300 },
        data: { label: "How?" },
      },
      {
        id: "5",
        type: "idea",
        position: { x: 600, y: 300 },
        data: { label: "Who?" },
      },
    ],
    edges: [
      { id: "e1-2", source: "1", target: "2" },
      { id: "e1-3", source: "1", target: "3" },
      { id: "e1-4", source: "1", target: "4" },
      { id: "e1-5", source: "1", target: "5" },
    ],
    icon: "lightbulb",
  },
  {
    id: "project",
    name: "Project Plan",
    description: "Organize project phases",
    nodes: [
      {
        id: "1",
        type: "topic",
        position: { x: 400, y: 50 },
        data: { label: "Project" },
      },
      {
        id: "2",
        type: "idea",
        position: { x: 150, y: 180 },
        data: { label: "Planning" },
      },
      {
        id: "3",
        type: "idea",
        position: { x: 400, y: 180 },
        data: { label: "Execution" },
      },
      {
        id: "4",
        type: "idea",
        position: { x: 650, y: 180 },
        data: { label: "Review" },
      },
      {
        id: "5",
        type: "note",
        position: { x: 100, y: 300 },
        data: { label: "Requirements" },
      },
      {
        id: "6",
        type: "note",
        position: { x: 200, y: 300 },
        data: { label: "Timeline" },
      },
      {
        id: "7",
        type: "note",
        position: { x: 350, y: 300 },
        data: { label: "Development" },
      },
      {
        id: "8",
        type: "note",
        position: { x: 450, y: 300 },
        data: { label: "Testing" },
      },
    ],
    edges: [
      { id: "e1-2", source: "1", target: "2" },
      { id: "e1-3", source: "1", target: "3" },
      { id: "e1-4", source: "1", target: "4" },
      { id: "e2-5", source: "2", target: "5" },
      { id: "e2-6", source: "2", target: "6" },
      { id: "e3-7", source: "3", target: "7" },
      { id: "e3-8", source: "3", target: "8" },
    ],
    icon: "assignment",
  },
  {
    id: "financial",
    name: "Financial Goals",
    description: "Map out financial objectives",
    nodes: [
      {
        id: "1",
        type: "topic",
        position: { x: 400, y: 50 },
        data: { label: "Financial Goals" },
      },
      {
        id: "2",
        type: "idea",
        position: { x: 200, y: 180 },
        data: { label: "Short-term" },
      },
      {
        id: "3",
        type: "idea",
        position: { x: 600, y: 180 },
        data: { label: "Long-term" },
      },
      {
        id: "4",
        type: "note",
        position: { x: 100, y: 300 },
        data: { label: "Emergency Fund" },
      },
      {
        id: "5",
        type: "note",
        position: { x: 250, y: 300 },
        data: { label: "Pay Off Debt" },
      },
      {
        id: "6",
        type: "note",
        position: { x: 500, y: 300 },
        data: { label: "Retirement" },
      },
      {
        id: "7",
        type: "note",
        position: { x: 650, y: 300 },
        data: { label: "Investments" },
      },
    ],
    edges: [
      { id: "e1-2", source: "1", target: "2" },
      { id: "e1-3", source: "1", target: "3" },
      { id: "e2-4", source: "2", target: "4" },
      { id: "e2-5", source: "2", target: "5" },
      { id: "e3-6", source: "3", target: "6" },
      { id: "e3-7", source: "3", target: "7" },
    ],
    icon: "savings",
  },
];
